// Message rendering: Markdown without raw HTML, links opened in a new tab, mentions
// (`(^|\s)@([a-z0-9._-]+)`, final dots being punctuation, docs/spec.md) wrapped so that they
// can be highlighted.
import MarkdownIt from 'markdown-it';

const MENTION = /(^|\s)@([a-z0-9._-]+)/gi;
const ALL = new Set(['canal', 'channel']);

/** The name as written, or without its final dots (punctuation): « @camille. ». */
function named(name: string, target: string | undefined): boolean {
  const lower = name.toLowerCase();
  const bare = lower.replace(/\.+$/, '');
  return bare !== '' && (lower === target || bare === target || ALL.has(lower) || ALL.has(bare));
}

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

const defaultLink =
  md.renderer.rules.link_open ?? ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx]!;
  token.attrSet('target', '_blank');
  token.attrSet('rel', 'noopener noreferrer');
  return defaultLink(tokens, idx, options, env, self);
};

// Mentions are detected in text tokens only: never inside inline code or code blocks.
md.renderer.rules.text = (tokens, idx, _options, env) => {
  const me = (env as { me?: string } | undefined)?.me;
  const text = md.utils.escapeHtml(tokens[idx]!.content);
  return text.replace(MENTION, (_all, before: string, name: string) => {
    const mine = named(name, me);
    // Final dots stay outside the highlight, unless they belong to the user's name.
    const shown = me && name.toLowerCase() === me ? name : name.replace(/\.+$/, '') || name;
    const rest = name.slice(shown.length);
    return `${before}<span class="mention${mine ? ' mine' : ''}">@${shown}</span>${rest}`;
  });
};

export function render(body: string, me: string): string {
  return md.render(body, { me: me.toLowerCase() });
}

/** True if the message names this user, @canal or @channel (same rule as the server). */
export function mentions(body: string, username: string): boolean {
  const target = username.toLowerCase();
  for (const match of body.matchAll(MENTION)) {
    if (named(match[2]!, target)) return true;
  }
  return false;
}

// --- Documents (preview of a .md file) ---------------------------------------------------

export interface Heading {
  level: number;
  text: string;
  id: string;
}

const doc = new MarkdownIt({ html: false, linkify: true });
doc.renderer.rules.link_open = md.renderer.rules.link_open;

function slug(text: string, used: Set<string>): string {
  const base =
    'h-' +
    (text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'titre');
  let id = base;
  for (let n = 2; used.has(id); n++) id = `${base}-${n}`;
  used.add(id);
  return id;
}

/** A Markdown document as HTML (no raw HTML), with ids on its headings and the table of
 * contents of levels 1 to 3. */
export function renderDocument(text: string): { html: string; toc: Heading[] } {
  const tokens = doc.parse(text, {});
  const toc: Heading[] = [];
  const used = new Set<string>();
  tokens.forEach((token, i) => {
    if (token.type !== 'heading_open') return;
    const level = Number(token.tag.slice(1));
    const title = tokens[i + 1]?.children?.map((c) => c.content).join('') ?? '';
    const id = slug(title, used);
    token.attrSet('id', id);
    if (level <= 3 && title.trim()) toc.push({ level, text: title, id });
  });
  return { html: doc.renderer.render(tokens, doc.options, {}), toc };
}
